import { useState, type FormEvent } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db8imok6oi3g3tv8unqg";

export default function ContactPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const name = String(formData.get("name") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const message = String(formData.get("message") ?? "").trim();

    if (!name || !email || !message) {
      setStatus("error");
      setFormError("请填写姓名、邮箱和留言内容后再提交。");
      return;
    }
    if (message.length > 500) {
      setStatus("error");
      setFormError("留言内容不能超过 500 字，请精简后再提交。");
      return;
    }

    const payload = new URLSearchParams();
    payload.append("name", name);
    payload.append("email", email);
    payload.append("message", message);

    setStatus("submitting");
    setFormError("");
    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam =
        typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK";

      if (ok && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          isSpam
            ? "提交未通过验证，请检查内容后重试。"
            : serverMsg || "提交失败，请稍后重试。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  const contactCards = [
    {
      icon: "ri-mail-line",
      title: "官方邮箱",
      value: "contact@theoryfilm.cn",
      href: "mailto:contact@theoryfilm.cn",
    },
    { icon: "ri-weibo-line", title: "微博", value: "@理论电影", href: "https://weibo.com" },
    { icon: "ri-douban-line", title: "豆瓣", value: "理论电影", href: "https://www.douban.com" },
  ];

  return (
    <SiteLayout>
      <div className="bg-primary-900 text-background-50">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-12 md:py-16">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "联系我们" }]} />
          <p className="text-xs tracking-[0.3em] text-accent-400 mt-6 mb-4 uppercase">Contact</p>
          <h1 className="font-heading text-3xl md:text-4xl font-bold mb-4">联系我们</h1>
          <p className="text-sm md:text-base text-background-200 leading-relaxed max-w-2xl">
            无论是内容反馈、投稿意向还是合作咨询，都欢迎给我们留言，我们会尽快回复。
          </p>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-14 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-8">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-1">
                在线留言
              </h2>
              <p className="text-sm text-foreground-500 mb-7">
                带 <span className="text-secondary-600">*</span> 的为必填项
              </p>

              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-2">
                    姓名 <span className="text-secondary-600">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入您的姓名"
                    className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-secondary-400 transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-2">
                    邮箱 <span className="text-secondary-600">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="请输入您的邮箱"
                    className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-secondary-400 transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-2">
                    留言内容 <span className="text-secondary-600">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={6}
                    placeholder="请写下您的想法、反馈或问题（不超过 500 字）"
                    className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-secondary-400 transition-colors resize-none"
                  />
                </div>

                <input
                  type="text"
                  name="contact_alt"
                  className="contact-form-field-extra"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-secondary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-secondary-600 transition-colors disabled:opacity-60 cursor-pointer"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" />
                      提交留言
                    </>
                  )}
                </button>

                {status === "success" && (
                  <div className="flex items-start gap-3 p-4 rounded-md bg-secondary-50 border border-secondary-200 text-sm text-secondary-900">
                    <i className="ri-checkbox-circle-fill text-secondary-600 mt-0.5" />
                    <span>留言已提交成功，感谢您的反馈，我们会尽快与您联系。</span>
                  </div>
                )}
                {status === "error" && (
                  <div className="flex items-start gap-3 p-4 rounded-md bg-accent-50 border border-accent-200 text-sm text-accent-900">
                    <i className="ri-error-warning-fill text-accent-600 mt-0.5" />
                    <span>{formError || "提交失败，请稍后重试。"}</span>
                  </div>
                )}
              </form>
            </div>
          </div>

          <div className="lg:col-span-5 space-y-5">
            {contactCards.map((c) => (
              <a
                key={c.title}
                href={c.href}
                target={c.href.startsWith("http") ? "_blank" : undefined}
                rel={c.href.startsWith("http") ? "noreferrer" : undefined}
                className="group flex items-center gap-4 bg-background-50 border border-background-200 rounded-lg p-5 hover:border-secondary-300 transition-colors"
              >
                <span className="w-12 h-12 rounded-md bg-primary-50 text-primary-700 flex items-center justify-center shrink-0">
                  <i className={`${c.icon} text-xl`} />
                </span>
                <span>
                  <span className="block text-xs text-foreground-400">{c.title}</span>
                  <span className="block text-sm font-medium text-foreground-900 group-hover:text-secondary-700 transition-colors">
                    {c.value}
                  </span>
                </span>
                <i className="ri-arrow-right-up-line ml-auto text-foreground-400 group-hover:text-secondary-600 transition-colors" />
              </a>
            ))}

            <div className="bg-background-100 rounded-lg p-6">
              <h3 className="font-heading text-base font-bold text-foreground-950 mb-3">
                温馨提示
              </h3>
              <ul className="space-y-2.5 text-sm text-foreground-600 leading-relaxed">
                <li className="flex gap-2">
                  <i className="ri-checkbox-blank-circle-fill text-[6px] text-secondary-500 mt-2" />
                  我们通常在 2 个工作日内回复您的留言。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-blank-circle-fill text-[6px] text-secondary-500 mt-2" />
                  本站不提供影片播放与下载服务，相关请求恕无法满足。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-blank-circle-fill text-[6px] text-secondary-500 mt-2" />
                  投稿请附上文章主题与简要说明，方便编辑团队评估。
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}